/**
 * Placement UI copy — sentence-case labels and the Phosphor glyph name for
 * each matrix `Placement`. Pure presentation, NOT a source of truth: the
 * resolution logic (capability, publishability, resolution order) lives in
 * `src/lib/platform-matrix/placement-resolution.ts`, which this module does
 * not need to re-import from.
 */
import type { Placement } from '@/lib/platform-matrix/placement-matrix';

/** Sentence-case, user-facing label per placement (picker trigger + rows). */
export const PLACEMENT_LABEL: Record<Placement, string> = {
  feed: 'Feed post',
  reel: 'Reel',
  story: 'Story',
  short: 'Short',
  pin: 'Pin',
  landscape: 'Video',
  document: 'Document',
  photo: 'Photo',
};

/**
 * The closed set of Phosphor glyph names PlacementPicker knows how to render.
 * Adding a glyph here without a matching entry in the picker's
 * `ICON_BY_GLYPH_NAME` (and vice versa) is a compile error — the two files
 * are linked through this union, not through bare strings.
 */
export type PlacementGlyphName = 'SquaresFourIcon' | 'FilmStripIcon' | 'CircleDashedIcon';

/**
 * Phosphor `*Icon` component name for each placement's trigger glyph
 * (`@phosphor-icons/react`). `feed`/`reel`/`story` are the only placements
 * PlacementPicker renders today (the rest are reserved for when their
 * platforms gain a picker — see PUBLISHABLE_PLACEMENTS).
 */
export const PLACEMENT_GLYPH: Record<Placement, PlacementGlyphName> = {
  feed: 'SquaresFourIcon',
  reel: 'FilmStripIcon',
  story: 'CircleDashedIcon',
  short: 'FilmStripIcon',
  pin: 'SquaresFourIcon',
  landscape: 'FilmStripIcon',
  document: 'SquaresFourIcon',
  photo: 'SquaresFourIcon',
};
